import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import SEO from "../../components/SEO";
import LimeLogo from "../../assets/lime-logo-no-bg.png";
import AppCard from "../../components/limelite/marketplace/AppCard";
import SpotlightHero from "../../components/limelite/marketplace/SpotlightHero";
import ProductCard from "../../components/limelite/marketplace/ProductCard";
import { CATEGORIES, PLATFORMS, PAGE_SIZE } from "../../lib/marketplace";

/**
 * The Limelite Marketplace hub.
 *
 * SEARCH IS BLENDED, DELIBERATELY
 * -------------------------------
 * A shopper searching "jackets" does not care whether the match is a *store* or an
 * individual *product* — they want everything that offers jackets. So a search query
 * fans out to BOTH indexes: /api/marketplace/listings (apps and stores) and
 * /api/marketplace/products (the cross-store product index, which has a weighted text
 * index on name/tags/brand/description).
 *
 * The product half degrades to nothing when the index is empty, which is its state in
 * prod as of 2026-08-10 — /api/marketplace/products?search=jacket returns
 * {products: [], total: 0} because nothing has ever been ingested. The UI is built so
 * products simply appear once ingest starts running; no frontend change is needed.
 */

/**
 * Prefer a genuinely boosted listing for the banner; fall back to the most-viewed one.
 * The fallback is load-bearing: /api/marketplace/shop returns featured: [] in prod, so
 * without it the hub ships with a hole where its largest element should be.
 */
function pickSpotlight(shop, listings) {
  const paid = shop?.featured?.[0];
  if (paid) return { listing: paid, isPaid: true };

  const pool = listings.length ? listings : shop?.recent || [];
  if (!pool.length) return null;
  const best = [...pool].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))[0];
  return { listing: best, isPaid: false };
}

function Shelf({ title, hint, children }) {
  return (
    <section className="mb-12">
      <div className="mb-4 flex items-baseline gap-3 border-b border-white/8 pb-2">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-silver-light">{title}</h2>
        {hint && <span className="text-xs text-silver-dark">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

export default function LimeliteMarketplacePage() {
  console.log(`[${new Date().toISOString()}] [LimeliteMarketplacePage] render`);
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [products, setProducts] = useState([]);
  const [productTotal, setProductTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [shop, setShop] = useState(null);

  const category = searchParams.get("category") || "all";
  const platform = searchParams.get("platform") || "all";
  const q = searchParams.get("q") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const isDefaultView = category === "all" && platform === "all" && !q && page === 1;

  // Draft lives separately from the committed `q` so typing does not refetch on every
  // keystroke — the query only commits on submit, and the URL stays shareable.
  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);

  // Hub data (spotlight + facet counts) — loaded once, independent of the filters.
  useEffect(() => {
    axios.get("/api/marketplace/shop").then((r) => setShop(r.data)).catch(() => setShop(null));
  }, []);

  const facetCount = {};
  (shop?.categories || []).forEach((c) => { facetCount[c.id] = c.count; });
  facetCount.all = shop?.total ?? undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (platform !== "all") params.set("platform", platform);
      if (q.trim()) params.set("search", q.trim());
      params.set("page", String(page));

      // Products are only fetched for an actual query — the product index is a search
      // surface, not something to dump onto the default hub view.
      const productReq = q.trim()
        ? axios.get(`/api/marketplace/products?search=${encodeURIComponent(q.trim())}`)
        : Promise.resolve(null);

      // allSettled: a failing product index must not blank the app results, and vice
      // versa. Partial data beats a 500.
      const [listRes, prodRes] = await Promise.allSettled([
        axios.get(`/api/marketplace/listings?${params}`),
        productReq,
      ]);

      if (listRes.status === "fulfilled") {
        setListings(listRes.value.data.listings || []);
        setTotal(listRes.value.data.total || 0);
      } else {
        setListings([]);
        setTotal(0);
        setFailed(true);
      }

      if (prodRes.status === "fulfilled" && prodRes.value) {
        setProducts(prodRes.value.data.products || []);
        setProductTotal(prodRes.value.data.total || 0);
      } else {
        setProducts([]);
        setProductTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [category, platform, q, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === "all" || !val) next.delete(key); else next.set(key, val);
      if (key !== "page") next.delete("page");
      return next;
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (draft.trim()) next.set("q", draft.trim()); else next.delete("q");
      next.delete("page");
      return next;
    });
  };

  const spotlight = isDefaultView ? pickSpotlight(shop, listings) : null;
  const grid = spotlight ? listings.filter((l) => l._id !== spotlight.listing._id) : listings;
  // Paid boosts already rotate fairly server-side. Keep the spotlight out of this
  // collection so promotion buys two distinct placements, not the same card twice.
  const featuredCollection = isDefaultView
    ? (shop?.featured || []).filter((l) => l._id !== spotlight?.listing?._id).slice(0, 4)
    : [];

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Limelite Marketplace",
    url: "https://rivieratech.solutions/limelite/marketplace",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: listings.slice(0, 20).map((l, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://rivieratech.solutions/limelite/marketplace/${l.slug}`,
        name: l.name,
      })),
    },
  };

  return (
    <>
      <SEO
        title="Limelite Marketplace — discover apps, stores and products built on Limelite"
        description="Browse independent stores, web apps, SaaS and mobile apps built and deployed on Limelite. Search across every store's products, filter by category and platform."
        url="/limelite/marketplace"
        schema={schema}
      />

      <div className="min-h-[calc(100vh-4rem)] bg-[#070708] text-silver-light">
        {/* Ambient brand glow, kept as contained pools — a corner-to-corner gradient
            turns the whole left half olive at 1440px and the page stops reading black. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[600px] overflow-hidden" aria-hidden="true">
          <div className="absolute -top-56 left-[8%] h-[520px] w-[620px] rounded-full bg-lime-400/[0.055] blur-[150px]" />
          <div className="absolute -right-[10%] top-[10%] h-[420px] w-[520px] rounded-full bg-lime-400/[0.03] blur-[140px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 pb-20 pt-12 md:px-8">
          {/* ── Masthead + search ── */}
          <div className="mb-10">
            <div className="mb-3 flex items-center gap-2">
              <img src={LimeLogo} alt="" className="h-6 w-6" />
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-lime-400">
                Limelite Marketplace
              </span>
            </div>
            <h1 className="max-w-2xl text-3xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl">
              Discover what people are building
            </h1>
            <p className="mt-3 max-w-xl text-sm text-silver-dark">
              Apps, SaaS tools and independent stores — all deployed on Limelite. Search across
              every store at once.
            </p>

            <form onSubmit={handleSearch} className="mt-6 flex max-w-xl gap-0">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Search apps, stores, or products — try “jackets”"
                aria-label="Search the marketplace"
                className="min-w-0 flex-1 border border-white/12 bg-white/[0.04] px-4 py-3 text-sm text-silver-light placeholder-silver-dark outline-none transition-colors focus:border-lime-500/60"
              />
              <button
                type="submit"
                className="bg-lime-500 px-7 py-3 text-sm font-bold text-black transition-colors hover:bg-lime-400"
              >
                Search
              </button>
            </form>
          </div>

          {/* ── Spotlight banner (default hub view only) ── */}
          {spotlight && (
            <div className="mb-12">
              <SpotlightHero listing={spotlight.listing} isPaid={spotlight.isPaid} />
            </div>
          )}

          {featuredCollection.length > 0 && (
            <Shelf title="Featured on Limelite" hint="Sponsored placements">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {featuredCollection.map((listing) => <AppCard key={listing._id} listing={listing} />)}
              </div>
            </Shelf>
          )}

          {/* ── Filters ── */}
          <div className="mb-8 flex flex-col gap-3 border-y border-white/8 py-3 lg:flex-row lg:items-center">
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => {
                const active = category === c.id;
                const count = facetCount[c.id];
                // Hide empty categories — empty pills advertise an empty marketplace.
                if (c.id !== "all" && !count) return null;
                return (
                  <button
                    key={c.id}
                    onClick={() => setFilter("category", c.id)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? "bg-lime-500 text-black"
                        : "border border-white/10 text-silver-dark hover:border-white/25 hover:text-silver-light"
                    }`}
                  >
                    {c.label}
                    {count ? <span className="ml-1.5 opacity-60">{count}</span> : null}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-1.5 lg:ml-auto">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setFilter("platform", p.id)}
                  className={`px-2.5 py-1.5 text-[11px] transition-colors ${
                    platform === p.id
                      ? "border border-lime-500/60 text-lime-300"
                      : "border border-white/8 text-silver-dark hover:border-white/20"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Products (search only) ── */}
          {q && products.length > 0 && (
            <Shelf title={`Products matching “${q}”`} hint={`${productTotal} across all stores`}>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {products.slice(0, 10).map((p) => (
                  <ProductCard key={p._id || p.slug} product={p} />
                ))}
              </div>
            </Shelf>
          )}

          {/* ── Apps / stores ── */}
          <Shelf
            title={q ? "Apps & stores" : isDefaultView ? "Browse everything" : "Results"}
            hint={
              failed
                ? "Could not reach the marketplace API."
                : loading
                ? "Loading…"
                : `${total} result${total === 1 ? "" : "s"}`
            }
          >
            <div className="mb-4 flex justify-end">
              <Link
                to="/limelite/deploy/account"
                className="border border-lime-500/40 px-3 py-1.5 text-xs font-semibold text-lime-300 transition-colors hover:bg-lime-500/10"
              >
                + List your app
              </Link>
            </div>

            {loading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-64 animate-pulse border border-white/8 bg-white/[0.03]" />
                ))}
              </div>
            ) : grid.length === 0 ? (
              <EmptyState failed={failed} q={q} hasProducts={products.length > 0} />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {grid.map((l, i) => (
                  <AppCard key={l._id} listing={l} priority={i < 4} />
                ))}
              </div>
            )}
          </Shelf>

          {/* ── Pagination ── */}
          {total > PAGE_SIZE && (
            <div className="mt-10 flex items-center justify-center gap-3">
              {page > 1 && (
                <button
                  onClick={() => setFilter("page", String(page - 1))}
                  className="border border-white/12 px-4 py-2 text-sm text-silver-medium transition-colors hover:border-white/30 hover:text-silver-light"
                >
                  ← Previous
                </button>
              )}
              <span className="text-xs text-silver-dark">
                Page {page} of {Math.ceil(total / PAGE_SIZE)}
              </span>
              {page * PAGE_SIZE < total && (
                <button
                  onClick={() => setFilter("page", String(page + 1))}
                  className="border border-white/12 px-4 py-2 text-sm text-silver-medium transition-colors hover:border-white/30 hover:text-silver-light"
                >
                  Next →
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function EmptyState({ failed, q, hasProducts }) {
  return (
    <div className="border border-white/8 bg-white/[0.02] py-20 text-center">
      <img src={LimeLogo} alt="" className="mx-auto mb-4 h-10 w-10 opacity-25" />
      <p className="text-sm text-silver-medium">
        {failed
          ? "The marketplace is temporarily unavailable."
          : q
          ? hasProducts
            ? `No apps or stores matched “${q}” — but there are products above.`
            : `Nothing matched “${q}”.`
          : "No apps here yet."}
      </p>
      {!failed && (
        <Link
          to={q ? "/limelite/marketplace" : "/limelite"}
          className="mt-6 inline-flex bg-lime-500 px-6 py-2.5 text-sm font-bold text-black transition-colors hover:bg-lime-400"
        >
          {q ? "Browse everything" : "Get started with Limelite →"}
        </Link>
      )}
    </div>
  );
}
